import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

const PARTNERS_COLLECTION = 'partners';

const genDocId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/** Calendar date string (YYYY-MM-DD) → start of that local day */
export function dateInputToTimestamp(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  const [y, m, d] = dateStr.split('-').map((n) => parseInt(n, 10));
  if (!y || !m || !d) return null;
  return Timestamp.fromDate(new Date(y, m - 1, d));
}

export function timestampToDateInput(ts) {
  if (!ts) return '';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function normalizeTimestamp(value) {
  if (!value) return null;
  if (value.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
}

export function normalizePartnerDoc(id, data) {
  if (!data) return { id, companyName: '', team: 'General', members: [] };
  return {
    id,
    companyName: data.companyName || '',
    team: data.team || 'General',
    logoUrl: data.logoUrl || '',
    members: Array.isArray(data.members)
      ? data.members.map((m) => ({
          ...m,
          dateJoined: normalizeTimestamp(m.dateJoined),
        }))
      : [],
    w9Collected: Boolean(data.w9Collected),
    w9CollectedAt: normalizeTimestamp(data.w9CollectedAt),
    w9Notes: data.w9Notes || '',
    w9FileUrl: data.w9FileUrl || '',
    contracts: Array.isArray(data.contracts) ? data.contracts : [],
    documents: Array.isArray(data.documents)
      ? data.documents.map((d) => {
          const v = d.uploadedAt;
          let uploadedAt = null;
          if (v) {
            if (v.toDate) uploadedAt = v.toDate();
            else if (v instanceof Date) uploadedAt = v;
            else if (typeof v === 'string') {
              const dt = new Date(v);
              uploadedAt = Number.isNaN(dt.getTime()) ? null : dt;
            }
          }
          return {
            id: d.id || genDocId(),
            name: d.name || '',
            fileUrl: d.fileUrl || '',
            uploadedByUid: d.uploadedByUid || '',
            uploaderName: d.uploaderName || '',
            uploadedAt,
          };
        })
      : [],
    payments: Array.isArray(data.payments) ? data.payments : [],
    linkedClientIds: Array.isArray(data.linkedClientIds) ? data.linkedClientIds : [],
    createdAt: normalizeTimestamp(data.createdAt),
    updatedAt: normalizeTimestamp(data.updatedAt),
  };
}

export async function getPartners() {
  const snap = await getDocs(collection(db, PARTNERS_COLLECTION));
  const list = snap.docs.map((d) => normalizePartnerDoc(d.id, d.data()));
  list.sort((a, b) =>
    (a.companyName || '').localeCompare(b.companyName || '', undefined, { sensitivity: 'base' })
  );
  return list;
}

export async function getPartner(partnerId) {
  const ref = doc(db, PARTNERS_COLLECTION, partnerId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return normalizePartnerDoc(snap.id, snap.data());
}

/**
 * Join internal_users for each member row.
 * @returns {Promise<Array<{ userId, dateJoined, partnerRole?, firstName, lastName, email, role, missing? }>>}
 */
export async function enrichMembers(members = []) {
  const list = Array.isArray(members) ? members : [];
  return Promise.all(
    list.map(async (m) => {
      const userId = m.userId;
      if (!userId) {
        return { ...m, firstName: '', lastName: '', email: '', role: '', missing: true };
      }
      const snap = await getDoc(doc(db, 'internal_users', userId));
      if (!snap.exists()) {
        return {
          ...m,
          firstName: '',
          lastName: '',
          email: '',
          role: '',
          missing: true,
          dateJoined: normalizeTimestamp(m.dateJoined),
        };
      }
      const u = snap.data();
      return {
        userId,
        partnerRole: m.partnerRole || '',
        dateJoined: normalizeTimestamp(m.dateJoined),
        firstName: u.firstName || '',
        lastName: u.lastName || '',
        email: u.email || '',
        role: u.role || '',
      };
    })
  );
}

const defaultOverview = () => ({
  w9Collected: false,
  w9CollectedAt: null,
  w9Notes: '',
  w9FileUrl: '',
  contracts: [],
  documents: [],
  payments: [],
  linkedClientIds: [],
});

/** Build a Firestore-safe member object from UI fields */
export function buildMemberWrite({ userId, dateJoinedInput, partnerRole }) {
  if (!userId) return null;
  const ts = dateInputToTimestamp(dateJoinedInput) || Timestamp.now();
  const row = { userId: String(userId), dateJoined: ts };
  if (partnerRole && String(partnerRole).trim()) {
    row.partnerRole = String(partnerRole).trim();
  }
  return row;
}

export async function createPartner({ companyName, team, members }) {
  const now = serverTimestamp();
  const payload = {
    companyName: String(companyName || '').trim(),
    team: String(team || 'General'),
    members: Array.isArray(members) ? members.filter(Boolean) : [],
    ...defaultOverview(),
    createdAt: now,
    updatedAt: now,
  };
  if (!payload.companyName) {
    throw new Error('Company name is required');
  }
  const ref = await addDoc(collection(db, PARTNERS_COLLECTION), payload);
  return ref.id;
}

export async function updatePartner(partnerId, patch) {
  const ref = doc(db, PARTNERS_COLLECTION, partnerId);
  const cleaned = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  );
  await updateDoc(ref, {
    ...cleaned,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePartner(partnerId) {
  await deleteDoc(doc(db, PARTNERS_COLLECTION, partnerId));
}
